import type { OrbitMeasurementGrant } from './composite-capability.js';
import { assessExecutableLegality, loadInContext, loadMeasurementProjection, type SkillLoadResult } from './executable-legality.js';
import { persistedSkillContext, type LegalityContext } from './legality-context.js';
import type { CanonicalSkillV2 } from './types.js';

export type { SkillLoadResult };

/** Stable detail used by production runtime intake boundaries after loading. */
export function skillLoadFailureCode(result: Exclude<SkillLoadResult, { readonly status: 'READY' }>): string {
  if (result.status === 'DEFERRED') return result.code;
  // A composite skill loaded fine; what failed is the caller's requirement
  // that it be a single-application V2. Naming that distinctly stops it from
  // being read as "the skill is broken".
  if (result.status === 'READY_COMPOSITE') return 'COMPOSITE_SKILL_REQUIRES_COMPOSITE_CALLER';
  return result.issues.map((issue) => issue.code).join(',');
}

/**
 * Sole loading boundary, in the context a stored skill is judged in (`persistedSkillContext`): it answers whether the engine can execute the definition, and leaves the profile's safety limits to `assessExecutableLegality`. Legacy data is imported once and then follows V2.
 *
 * `compositeCastSeed` binds a composite plan's randomised placements (a
 * `SCATTER`'s centres) so that loading the same bytes twice yields the same
 * plan. It is supplied by whichever layer owns the cast's RNG; the default
 * exists for inspection callers that only need to know whether a skill
 * loads, and never for a real cast.
 */
export function loadCanonicalSkill(input: unknown, compositeCastSeed = 0, grant?: OrbitMeasurementGrant): SkillLoadResult {
  return loadInContext(input, persistedSkillContext(), { castSeed: compositeCastSeed, ...(grant === undefined ? {} : { grant }) });
}

/**
 * Require the one canonical loader boundary for runtime ownership callers.
 * The non-throwing `loadCanonicalSkill` remains the public inspection API;
 * this helper only supplies consistent fail-closed error formatting to
 * internal runtime intake points.
 */
export function requireCanonicalSkill(input: unknown, errorPrefix: string): CanonicalSkillV2 {
  return requireV2(loadCanonicalSkill(input), errorPrefix);
}

/** Internal compiler/evaluation projections keep the strict import/compile boundary and measure their own refresh collisions. */
export function requireMeasurementProjection(input: unknown, errorPrefix: string): CanonicalSkillV2 {
  return requireV2(loadMeasurementProjection(input), errorPrefix);
}

/** `requireCanonicalSkill` for a caller that owns and casts the skill: the profile's safety limits apply as well (`assessExecutableLegality`). */
export function requireExecutableSkill(input: unknown, errorPrefix: string, context: LegalityContext = persistedSkillContext()): CanonicalSkillV2 {
  return requireV2(assessExecutableLegality(input, context), errorPrefix);
}

function requireV2(loaded: SkillLoadResult, errorPrefix: string): CanonicalSkillV2 {
  if (loaded.status === 'READY') return loaded.skill;
  // A composite skill has no V2 form, so a caller that demands one is
  // refused rather than handed a single stage of it.
  if (loaded.status === 'READY_COMPOSITE') {
    throw new Error(`${errorPrefix}: COMPOSITE_SKILL_REQUIRES_COMPOSITE_CALLER`);
  }
  throw new Error(`${errorPrefix}: ${skillLoadFailureCode(loaded)}`);
}
